import './PdfViewerModal.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PdfServiceModule } from '../../services/host/PdfServiceModule';

/**
 * V7.2.B PDF viewer modal (2026-06-03).
 *
 * Renders a CSS-positioned overlay (user pick over native <dialog>) that
 * fetches the PDF as a blob via PdfServiceModule.fetchPdfBlob, creates an
 * object URL, and points an iframe at it. The browser's native PDF viewer
 * (Chrome/Firefox/Safari) renders inside the iframe.
 *
 * iframe src is `blob:<UUID>` (NOT the API path) so the request never
 * leaves the SPA process — that's how we keep the backend stream route
 * JWT-gated without iframe-auth gymnastics (R1.0 Concern 1 resolution).
 *
 * Blob URL lifecycle:
 *   mount with path
 *     → fetchPdfBlob(path) carrying Bearer
 *     → URL.createObjectURL(blob) → setBlobUrl
 *     → iframe loads from blob:
 *   unmount / path change
 *     → cancel guard prevents setState on stale fetch
 *     → URL.revokeObjectURL frees the blob
 *
 * Tested in __tests__/PdfViewerModal.test.tsx — including deferred-resolve
 * loading state assertion per R1.0 Concern 2 fix.
 */

export type PdfViewerState = 'loading' | 'ready' | 'error';

export interface PdfViewerModalProps {
  path: string;
  title: string;
  onClose: () => void;
  pdfService?: PdfServiceModule;
  testidPrefix?: string;
}

export function PdfViewerModal({
  path,
  title,
  onClose,
  pdfService,
  testidPrefix = 'br-pdf-viewer-modal',
}: PdfViewerModalProps) {
  // Stabilize the default service so the blob-fetch useEffect doesn't
  // re-fire every render (mirror of usePdfLibrary stability fix).
  const resolvedService = useMemo(
    () => pdfService ?? new PdfServiceModule(),
    [pdfService],
  );

  const [state, setState] = useState<PdfViewerState>('loading');
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  // Blob fetch + ObjectURL lifecycle. Cancels on unmount; revokes the
  // ObjectURL on cleanup whether the fetch succeeded or not.
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;

    setState('loading');
    setBlobUrl(null);
    setError(null);

    (async () => {
      try {
        const blob = await resolvedService.fetchPdfBlob(path);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setState('ready');
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setState('error');
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl !== null) {
        URL.revokeObjectURL(createdUrl);
      }
    };
  }, [path, resolvedService]);

  // ESC closes the modal (consistent with V15 modal expectations).
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Focus the dialog on mount for accessibility.
  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const titleId = `${testidPrefix}-title`;

  return (
    <div
      className="br-pdf-viewer-modal-backdrop"
      data-testid={`${testidPrefix}-backdrop`}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        className="br-pdf-viewer-modal"
        data-testid={testidPrefix}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="br-pdf-viewer-modal-header">
          <h2 id={titleId} className="br-pdf-viewer-modal-title">
            {title}
          </h2>
          <button
            type="button"
            className="br-pdf-viewer-modal-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close PDF viewer"
          >
            ×
          </button>
        </header>

        <div className="br-pdf-viewer-modal-body">
          {state === 'loading' && (
            <div
              className="br-pdf-viewer-modal-loading"
              data-testid={`${testidPrefix}-loading`}
            >
              Loading PDF…
            </div>
          )}
          {state === 'error' && (
            <div
              className="br-pdf-viewer-modal-error"
              data-testid={`${testidPrefix}-error`}
            >
              Unable to load PDF: {error ?? 'unknown error'}
            </div>
          )}
          {state === 'ready' && blobUrl !== null && (
            <iframe
              className="br-pdf-viewer-modal-iframe"
              data-testid={`${testidPrefix}-iframe`}
              src={blobUrl}
              title={title}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default PdfViewerModal;
